import { useMemo } from 'react';

import type {
  CustomAttributeSchemaInput,
  Dataset,
  TableFields,
  TableRecord,
} from '../types';
import { buildCustomAttributeAliases } from '../utils/coalesceCustomAttributeFields';
import { toCustomAttributeSchemas } from '../utils/customAttributes';
import { useAddCustomAttributeFieldData } from './useAddCustomAttributeFieldData';
import { useAddCustomAttributeFieldDefinitions } from './useAddCustomAttributeFieldDefinitions';

type Options<T extends TableRecord> = {
  customAttributeSchema: CustomAttributeSchemaInput;
  fields: TableFields<T>;
  data: Dataset<T> | undefined;
  useRelativeDates: boolean;
  // Bare schema property paths whose field config is Hidden (CUS-216).
  hiddenPaths?: Set<string>;
};

/**
 * Adds custom attribute support by modifying the dataset and field definitions
 *
 * @param options
 * @returns
 */
export const useAddCustomAttributes = <T extends TableRecord>(
  options: Options<T>
) => {
  // Shared between the column definitions and the row data so the surviving
  // column reads each row's own value (CUS-209). Hidden paths are excluded so a
  // field hidden on only one of the merged forms can't take the other form's
  // column down with it (CUS-216).
  const aliases = useMemo(
    () =>
      buildCustomAttributeAliases({
        customAttributeSchemas: toCustomAttributeSchemas(
          options.customAttributeSchema
        ),
        hiddenPaths: options.hiddenPaths,
      }),
    [options.customAttributeSchema, options.hiddenPaths]
  );

  const tableFields = useAddCustomAttributeFieldDefinitions({
    fields: options.fields,
    customAttributeSchema: options.customAttributeSchema,
    enableRelativeDates: options.useRelativeDates,
    aliases,
    hiddenPaths: options.hiddenPaths,
  });
  const tableData = useAddCustomAttributeFieldData({
    data: options.data,
    tableFields,
    aliases,
  });

  return { tableFields, tableData };
};
